import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { sentenceCase } from 'change-case';
import { useFormikContext } from 'formik';
import React, { FC, useState } from 'react';
import type { LookupConfigKind, StoreFormValues } from 'src/features/store/types/store.types';
import {
  allowedPosSystems,
  LOOKUP_FIELD_MAP,
  lookupSectionFields,
  lookupSummary,
  MODE_OPTIONS,
  posSystemLabel,
} from 'src/features/store/utils/lookupConfig';
import { ZonedDateTimePicker } from 'src/features/timezone';
import {
  DataEnumerationsDiscountCodeProvider,
  DataEnumerationsLookupMode,
  DataEnumerationsLoyaltyProgramProvider,
  DataEnumerationsPosSystem,
} from 'src/types/api/generated/api';

const useStyles = makeStyles((theme) => ({
  summaryContent: {
    alignItems: 'baseline',
  },
  summaryTitle: {
    flexShrink: 0,
    marginRight: theme.spacing(2),
  },
}));

export interface LookupConfigSectionProps {
  kind: LookupConfigKind;
  hasLoyaltyProgramUsername?: boolean;
  hasLoyaltyProgramPassword?: boolean;
}

const LookupConfigSection: FC<LookupConfigSectionProps> = ({
  kind,
  hasLoyaltyProgramUsername = false,
  hasLoyaltyProgramPassword = false,
}) => {
  const classes = useStyles();
  const {
    errors,
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
    submitCount,
    touched,
    values,
  } = useFormikContext<StoreFormValues>();
  const isLoyalty = kind === 'loyalty';
  const fields = LOOKUP_FIELD_MAP[kind];
  const mode = values[fields.mode];
  const provider = values[fields.provider];
  const sectionDisabled = mode === DataEnumerationsLookupMode.Disabled;
  const sectionHasError = submitCount > 0 && lookupSectionFields(kind).some((f) => errors[f]);
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const expanded = sectionHasError || (expandedOverride ?? !sectionDisabled);
  const showCredentials =
    provider === DataEnumerationsLoyaltyProgramProvider.YinzCam ||
    hasLoyaltyProgramUsername ||
    hasLoyaltyProgramPassword;
  const posSystems = allowedPosSystems(kind, provider);
  const storeZone = values.timezone;
  const modeOption = MODE_OPTIONS.find((option) => option.value === mode);
  const providerOptions = isLoyalty
    ? Object.values(DataEnumerationsLoyaltyProgramProvider)
    : Object.values(DataEnumerationsDiscountCodeProvider);
  const fieldError = (field: keyof StoreFormValues): boolean =>
    Boolean(touched[field] && errors[field]);
  const validationMessage = (field: keyof StoreFormValues): string | false =>
    touched[field] && errors[field] && sentenceCase(String(errors[field]));
  const updateProvider = (nextProvider: string): void => {
    const nextPosSystems = allowedPosSystems(kind, nextProvider);
    setFieldValue(fields.provider, nextProvider);
    if (nextPosSystems.length === 1) {
      setFieldValue(fields.posSystem, nextPosSystems[0]);
    } else if (!nextPosSystems.includes(values[fields.posSystem] as DataEnumerationsPosSystem)) {
      setFieldValue(fields.posSystem, '');
    }
  };
  const renderCredential = (
    field: 'loyaltyProgramUsername' | 'loyaltyProgramPassword',
    clearField: 'clearLoyaltyProgramUsername' | 'clearLoyaltyProgramPassword',
    label: string,
    configured: boolean,
  ) => (
    <Box mt={1} mb={1}>
      {configured && (
        <Typography variant="caption" display="block">
          Configured. Leave empty to keep the current value.
        </Typography>
      )}
      <TextField
        disabled={values[clearField]}
        error={fieldError(field)}
        fullWidth
        helperText={validationMessage(field)}
        inputProps={{
          form: {
            autocomplete: 'off',
          },
        }}
        label={label}
        name={field}
        onBlur={handleBlur}
        onChange={handleChange}
        type={field === 'loyaltyProgramPassword' ? 'password' : undefined}
        value={values[field]}
        variant="outlined"
      />
      {configured && (
        <FormControlLabel
          control={
            <Checkbox checked={values[clearField]} name={clearField} onChange={handleChange} />
          }
          label="Clear"
        />
      )}
    </Box>
  );

  return (
    <Grid item xs={12}>
      <Accordion
        expanded={expanded}
        onChange={(_event, isExpanded) => setExpandedOverride(isExpanded)}
      >
        <AccordionSummary
          classes={{ content: classes.summaryContent }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography className={classes.summaryTitle} variant="h5">
            {isLoyalty ? 'Loyalty Program' : 'Discount Codes'}
          </Typography>
          <Typography color={sectionHasError ? 'error' : 'textSecondary'} variant="body2">
            {sectionHasError ? 'Needs attention' : lookupSummary(kind, values)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  error={fieldError(fields.mode)}
                  fullWidth
                  helperText={validationMessage(fields.mode) || modeOption?.helperText}
                  label="Mode *"
                  name={fields.mode}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  select
                  value={mode}
                  variant="outlined"
                >
                  {MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {isLoyalty && option.loyaltyLabel ? option.loyaltyLabel : option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled}
                  error={fieldError(fields.provider)}
                  fullWidth
                  helperText={validationMessage(fields.provider)}
                  label={`Provider${sectionDisabled ? '' : ' *'}`}
                  name={fields.provider}
                  onBlur={handleBlur}
                  onChange={(event) => updateProvider(event.target.value)}
                  select
                  value={provider}
                  variant="outlined"
                >
                  {providerOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled || !provider}
                  error={fieldError(fields.posSystem)}
                  fullWidth
                  helperText={
                    validationMessage(fields.posSystem) ||
                    'This configuration only applies to stations running this POS.'
                  }
                  label={`POS System${sectionDisabled ? '' : ' *'}`}
                  name={fields.posSystem}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  select
                  value={values[fields.posSystem]}
                  variant="outlined"
                >
                  {posSystems.map((posSystem) => (
                    <MenuItem key={posSystem} value={posSystem}>
                      {posSystemLabel(posSystem)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled}
                  error={fieldError(fields.maximumAttempts)}
                  fullWidth
                  helperText={validationMessage(fields.maximumAttempts) || 'Leave empty for unlimited attempts.'}
                  inputProps={{ min: 1 }}
                  label="Maximum Attempts"
                  name={fields.maximumAttempts}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="number"
                  value={values[fields.maximumAttempts]}
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <ZonedDateTimePicker
                  disabled={sectionDisabled}
                  error={fieldError(fields.activeFrom)}
                  helperText={
                    validationMessage(fields.activeFrom) || 'Leave empty for always active.'
                  }
                  label="Active from"
                  onBlur={() => setFieldTouched(fields.activeFrom, true)}
                  onChange={(value) => setFieldValue(fields.activeFrom, value)}
                  value={values[fields.activeFrom]}
                  zone={storeZone}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <ZonedDateTimePicker
                  disabled={sectionDisabled}
                  error={fieldError(fields.activeTo)}
                  helperText={
                    validationMessage(fields.activeTo) || 'Leave empty for always active.'
                  }
                  label="Active to"
                  onBlur={() => setFieldTouched(fields.activeTo, true)}
                  onChange={(value) => setFieldValue(fields.activeTo, value)}
                  value={values[fields.activeTo]}
                  zone={storeZone}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled}
                  error={fieldError(fields.buttonLabel)}
                  fullWidth
                  helperText={
                    validationMessage(fields.buttonLabel) ||
                    'Shown on the lookup button in Checkout.'
                  }
                  label="Button Label"
                  name={fields.buttonLabel}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values[fields.buttonLabel]}
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled}
                  error={fieldError(fields.buttonColor)}
                  fullWidth
                  helperText={validationMessage(fields.buttonColor) || 'Enter a Checkout button color, such as #RRGGBB.'}
                  label="Button Color"
                  name={fields.buttonColor}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values[fields.buttonColor]}
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled}
                  error={fieldError(fields.successMessage)}
                  fullWidth
                  helperText={
                    validationMessage(fields.successMessage) ||
                    'Shown in the result dialog after a successful lookup.'
                  }
                  label="Success Message"
                  multiline
                  name={fields.successMessage}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  rows={3}
                  value={values[fields.successMessage]}
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={1} mb={1}>
                <TextField
                  disabled={sectionDisabled}
                  error={fieldError(fields.failureMessage)}
                  fullWidth
                  helperText={
                    validationMessage(fields.failureMessage) ||
                    'Shown in the result dialog after a failed lookup.'
                  }
                  label="Failure Message"
                  multiline
                  name={fields.failureMessage}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  rows={3}
                  value={values[fields.failureMessage]}
                  variant="outlined"
                />
              </Box>
            </Grid>
            {isLoyalty && provider === DataEnumerationsLoyaltyProgramProvider.YinzCam && (
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <TextField
                    disabled={sectionDisabled}
                    error={fieldError('loyaltyProgramHostname')}
                    fullWidth
                    helperText={validationMessage('loyaltyProgramHostname')}
                    label={`Hostname${sectionDisabled ? '' : ' *'}`}
                    name="loyaltyProgramHostname"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.loyaltyProgramHostname}
                    variant="outlined"
                  />
                </Box>
              </Grid>
            )}
            {isLoyalty && showCredentials && (
              <>
                <Grid item xs={12} md={6}>
                  {renderCredential(
                    'loyaltyProgramUsername',
                    'clearLoyaltyProgramUsername',
                    'Username',
                    hasLoyaltyProgramUsername,
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderCredential(
                    'loyaltyProgramPassword',
                    'clearLoyaltyProgramPassword',
                    'Password',
                    hasLoyaltyProgramPassword,
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default LookupConfigSection;
